import { inject, Injectable, signal } from "@angular/core";
import { BehaviorSubject, map, Observable, of } from "rxjs";
import {
	HistoryLogTextSegment,
	MilestoneEventHistoryLogData,
	MilestoneHistoryLogItem,
	MilestoneHistoryLogType,
	TransactionHistoryLogData
} from "../models/history-log-interface";
import {
	Milestone,
	MilestoneDownloadHistory,
	MilestoneStatusesText,
	MilestoneStatusHistory
} from "@shared/models/pay-cycle.interface";
import { environment } from "@environments/environment";
import { HttpClient } from "@angular/common/http";
import {
	CalendarErrorRecordItem,
	EventsTableDetailItem
} from "@modules/global-dashboard/models/global-dashboard-interface";
import { TransactionReportWithPagination } from "@shared/models/payouts.types";
import { getOrdinalDate, getTimeFromDate } from "src/app/shared/utils/date.util";
import { DatePipe } from "@angular/common";
import { OrdinalDatePipe } from "@shared/pipes/common/ordinal-date.pipe";

import { UserNameService } from "@shared/services/user-name/user-name.service";

@Injectable({
	providedIn: "root"
})
export class HistoryLogSidePanelService {
	private http = inject(HttpClient);
	private datePipe = inject(DatePipe);
	private ordinalDatePipe = inject(OrdinalDatePipe);
	private userNameService = inject(UserNameService);

	initialData: MilestoneEventHistoryLogData = {
		milestoneId: "",
		milestoneType: "",
		customerExternalId: "",
		customerName: "",
		payGroupId: "",
		paygroupName: "",
		cycleName: "",
		group: ""
	};

	transactionHistoryInitialData: TransactionHistoryLogData = {
		paymethodId: "",
		payoutId: ""
	};

	milestoneEventHistoryLogSubject = new BehaviorSubject<MilestoneEventHistoryLogData>(this.initialData);
	transactionHistoryLogSubject = new BehaviorSubject<TransactionHistoryLogData>(this.transactionHistoryInitialData);

	milestoneHistoryLogCalendarErrorItems = signal<CalendarErrorRecordItem[]>([]);
	filteredMilestonesHistoryLogCalendarItems = signal<CalendarErrorRecordItem[]>([]);

	setElementData(element: EventsTableDetailItem): void {
		const milestoneHistoryLogHeaderData = {
			milestoneId: element.milestoneDetails.id,
			milestoneType: element.milestoneDetails.type,
			customerExternalId: element.customerDetails.externalId,
			customerName: element.customerDetails.name,
			payGroupId: element.payGroupDetails.id,
			paygroupName: element.payGroupDetails.name,
			cycleName: element.payCycleDetails.name,
			group: element.group,
			payCycleId: element.payCycleDetails.id
		};
		this.milestoneEventHistoryLogSubject.next(milestoneHistoryLogHeaderData);
		this.setCalendarErrorItems(milestoneHistoryLogHeaderData);
	}

	setMilestoneHistoryLogData(data: MilestoneEventHistoryLogData) {
		this.milestoneEventHistoryLogSubject.next(data);
		this.setCalendarErrorItems(data);
	}

	setTransactionData(payoutId: string, paymethodId = ""): void {
		const transactionData = {
			payoutId: payoutId,
			paymethodId: paymethodId
		};

		this.transactionHistoryLogSubject.next(transactionData);
	}

	/**
	 * Sets the calendar error items for payment order milestone if exist
	 * @param milestoneHistoryLogData
	 */
	setCalendarErrorItems(milestoneHistoryLogData: MilestoneEventHistoryLogData): void {
		this.filteredMilestonesHistoryLogCalendarItems.set(
			this.milestoneHistoryLogCalendarErrorItems().filter(
				errList => errList.payCycleId === milestoneHistoryLogData.payCycleId
			)
		);
	}

	getSearchFilterTransformedKeys(
		userFullName: string,
		createdAt: string
	): Observable<{ user: string; ordinalDate: string; timeDate: string }> {
		const createdAtOrdinal = getOrdinalDate(createdAt);
		const createdAtTime = getTimeFromDate(createdAt);
		return of({ user: userFullName, ordinalDate: createdAtOrdinal, timeDate: createdAtTime });
	}

	getSearchFilterTransformedKeysTranscations(
		createdAt: string
	): Observable<{ ordinalDate: string; timeDate: string }> {
		const createdAtOrdinal = getOrdinalDate(createdAt);
		const createdAtTime = getTimeFromDate(createdAt);

		return of({ ordinalDate: createdAtOrdinal, timeDate: createdAtTime });
	}

	getMilestone(milestoneId: string): Observable<Milestone> {
		return this.http.get<Milestone>(`${environment.apiUri}/v1/calendars/milestones/${milestoneId}`);
	}

	getTransactionHistory(
		payoutId: string,
		paymethodId: string,
		rangeStart: string,
		rangeEnd: string
	): Observable<TransactionReportWithPagination> {
		return this.http.get<TransactionReportWithPagination>(
			`${environment.apiUri}/v1/transactions/report?rangeStart=${rangeStart}&rangeEnd=${rangeEnd}&payoutIds=${payoutId}&payMethodIds=${paymethodId}`
		);
	}

	resetElementData(): void {
		this.milestoneEventHistoryLogSubject.next(this.initialData);
		this.transactionHistoryLogSubject.next(this.transactionHistoryInitialData);
	}

	getAllHistories(milestone: Milestone): Observable<MilestoneHistoryLogItem[]> {
		const userIds = this.extractUserIds(milestone);

		return this.userNameService.resolveUserNames(userIds).pipe(
			map(userMap => {
				const status = this.mapStatusHistory(milestone.statusHistory || [], userMap);

				const downloads = this.mapDownloadHistory(milestone.downloadHistory || [], userMap);

				const combined = [...status, ...downloads];

				return combined.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
			})
		);
	}

	extractUserIds(milestone: Milestone): string[] {
		const ids = new Set<string>();
		(milestone.statusHistory || []).forEach(r => ids.add(r.userId));
		(milestone.downloadHistory || []).forEach(r => ids.add(r.userId));

		return Array.from(ids);
	}

	mapStatusHistory(statusHistory: MilestoneStatusHistory[], userMap: Map<string, string>): MilestoneHistoryLogItem[] {
		return statusHistory.map(item => {
			const userName = userMap.get(item.userId) ?? "SYSTEM";
			const milestoneStatusText =
				MilestoneStatusesText[item.statusTo as keyof typeof MilestoneStatusesText] ?? item.statusTo;

			return {
				historyLogType: MilestoneHistoryLogType.STATUS,
				createdAt: item.createdAt,
				userId: item.userId,
				userName,
				status: item.statusTo,
				title: this.buildStatusTitleText(item, userName, milestoneStatusText.toUpperCase()),
				subtitle: ""
			};
		});
	}

	buildStatusTitleText(
		statusHistoryItem: MilestoneStatusHistory,
		userName: string,
		statusText: string
	): HistoryLogTextSegment[] {
		//USER has changed the status to STATUS on CREATED AT DATE at CREATED AT TIME"
		const titleText: HistoryLogTextSegment[] = [];
		const dateText = this.ordinalDatePipe.transform(statusHistoryItem.createdAt) || "";
		const timeText = this.datePipe.transform(statusHistoryItem.createdAt, "HH:mm") || "";

		titleText.push({ text: userName, style: "highlight" });
		titleText.push({ text: "has changed the status to", style: "normal" });
		titleText.push({ text: statusText, style: "highlight" });
		titleText.push({ text: "on", style: "normal" });
		titleText.push({ text: dateText, style: "highlight" });
		titleText.push({ text: "at", style: "normal" });
		titleText.push({ text: timeText, style: "highlight" });

		return titleText;
	}

	mapDownloadHistory(
		downloadHistory: MilestoneDownloadHistory[],
		userMap: Map<string, string>
	): MilestoneHistoryLogItem[] {
		return downloadHistory.map(item => {
			const userName = userMap.get(item.userId) ?? "SYSTEM";

			return {
				historyLogType: MilestoneHistoryLogType.OTHER,
				createdAt: item.date,
				userId: item.userId,
				userName,
				status: "",
				title: this.buildDownloadTitleText(item, userName),
				subtitle: ""
			};
		});
	}

	buildDownloadTitleText(downloadHistoryItem: MilestoneDownloadHistory, userName: string): HistoryLogTextSegment[] {
		//USER downloaded FILE NAME report
		const titleText: HistoryLogTextSegment[] = [];

		titleText.push({ text: userName, style: "highlight" });
		titleText.push({ text: "downloaded", style: "normal" });
		titleText.push({ text: downloadHistoryItem.fileName, style: "highlight" });
		titleText.push({ text: "report", style: "normal" });

		return titleText;
	}
}
